import { useMutation, useQuery } from '@tanstack/react-query';
import { registerPasskey, loginWithPasskey } from '@f2m3/shared-api';
import mfaApi, { type MfaStatus } from '../fetch/mfaApi';
import apiClient from '../fetch/apiClient';
import userApi, { type MfaVerifyPayload } from '../fetch/userApi';
import notify from '../../utils/notify';
import { orShopNotOpen } from '../../utils/shopNotOpen';
import queryClient from '../../utils/queryClient';
import { GUEST_TOKEN_KEY } from './user';
import type { AuthTokenResponse } from '../types';

export const MFA_STATUS_KEY = ['mfa', 'status'] as const;

export const invalidateMfaStatus = (): void => {
  void queryClient.invalidateQueries({ queryKey: MFA_STATUS_KEY });
};

const guestTokenPayload = (): Record<string, string> => {
  const t = localStorage.getItem(GUEST_TOKEN_KEY);
  return t ? { guest_token: t } : {};
};

export const useMfaStatus = (enabled = true) =>
  useQuery<MfaStatus>({ queryKey: MFA_STATUS_KEY, queryFn: mfaApi.status, enabled });

export const useTotpSetup = () =>
  useMutation({ mutationFn: (password: string) => mfaApi.totpSetup(password) });

export const useTotpConfirm = () =>
  useMutation({
    mutationFn: ({ password, code }: { password: string; code: string }) => mfaApi.totpConfirm(password, code),
    onSuccess: () => {
      invalidateMfaStatus();
      notify('notify.success', 'notify.mfa.totp_enabled', { type: 'success' });
    },
  });

export const useDisableMfa = () =>
  useMutation({
    mutationFn: (code: string) => mfaApi.disable(code),
    onSuccess: () => {
      invalidateMfaStatus();
      notify('notify.success', 'notify.mfa.totp_disabled', { type: 'success' });
    },
    onError: () => notify('notify.error', 'notify.mfa.invalid_code', { type: 'danger' }),
  });

export const useRegenerateRecoveryCodes = () =>
  useMutation({
    mutationFn: (password: string) => mfaApi.regenerateRecoveryCodes(password),
    onSuccess: () => {
      invalidateMfaStatus();
      notify('notify.success', 'notify.mfa.recovery_regenerated', { type: 'success' });
    },
  });

export const useRegisterPasskey = () =>
  useMutation({
    mutationFn: (args: { password: string; nickname?: string }) => registerPasskey(apiClient, args),
    onSuccess: () => {
      invalidateMfaStatus();
      notify('notify.success', 'notify.mfa.passkey_added', { type: 'success' });
    },
    onError: () => notify('notify.error', 'notify.mfa.webauthn_failed', { type: 'danger' }),
  });

export const useDeletePasskey = () =>
  useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => mfaApi.deletePasskey(id, password),
    onSuccess: () => {
      invalidateMfaStatus();
      notify('notify.success', 'notify.mfa.passkey_removed', { type: 'success' });
    },
    onError: () => notify('notify.error', 'notify.mfa.invalid_password', { type: 'danger' }),
  });

// The revoke endpoint isn't password-gated (unlike deletePasskey), so callers
// pass an empty password; the signature is kept aligned with shared-api's
// deleteTrustedDevice(id, password) to keep the contract stable.
export const useDeleteTrustedDevice = () =>
  useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => mfaApi.deleteTrustedDevice(id, password),
    onSuccess: () => invalidateMfaStatus(),
  });

// Login-completion: the top-level token in the response is auto-stored by the
// newToken callback, so success just clears the guest token (transfer done).
export const useMfaVerify = () =>
  useMutation<AuthTokenResponse, unknown, MfaVerifyPayload>({
    mutationFn: userApi.verifyMfa,
    onSuccess: () => localStorage.removeItem(GUEST_TOKEN_KEY),
  });

export const usePasskeyLogin = () =>
  useMutation({
    mutationFn: () => loginWithPasskey(apiClient, { finishExtra: guestTokenPayload() }),
    onSuccess: () => localStorage.removeItem(GUEST_TOKEN_KEY),
    onError: (error) => notify('notify.error', orShopNotOpen(error, 'notify.mfa.passkey_login_failed'), { type: 'warning' }),
  });

export const useSignupWithPasskey = () =>
  useMutation({
    mutationFn: (args: { email: string; name: string }) => userApi.signupWithPasskey(args),
    onSuccess: () => localStorage.removeItem(GUEST_TOKEN_KEY),
    onError: (error) => notify('notify.error', orShopNotOpen(error, 'notify.mfa.passkey_signup_failed'), { type: 'warning' }),
  });
